import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { NavigationGroup } from "../../config/routes/navigationGroups";
import { AdminNavLinkConfig } from "../../config/adminNavigationConfig";
import NestedNavItem from "./NestedNavItem";

interface CollapsibleNavGroupProps {
  group: NavigationGroup;
  isExpanded: boolean;
  forceExpanded?: boolean;
  onToggle: () => void;
  userPermissions: string[];
  notificationSources: Record<string, number | undefined>;
  getNotificationCount: (
    item: AdminNavLinkConfig,
    sources: Record<string, number | undefined>,
  ) => number | undefined;
  onLinkClick: () => void;
  isCompact?: boolean;
}

const NESTED_ITEMS_STORAGE_KEY = "sidebarNestedItemsExpanded";

const CollapsibleNavGroup: React.FC<CollapsibleNavGroupProps> = ({
  group,
  isExpanded,
  forceExpanded = false,
  onToggle,
  userPermissions,
  notificationSources,
  getNotificationCount,
  onLinkClick,
  isCompact = false,
}) => {
  const { t } = useTranslation();

  // State for nested items expansion (persisted to localStorage)
  const [expandedNestedItems, setExpandedNestedItems] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(NESTED_ITEMS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : ["/admin/requests"]; // Default: Orders expanded
    } catch {
      return ["/admin/requests"];
    }
  });

  // Persist nested items expansion state
  useEffect(() => {
    try {
      localStorage.setItem(NESTED_ITEMS_STORAGE_KEY, JSON.stringify(expandedNestedItems));
    } catch {
      // Ignore localStorage errors
    }
  }, [expandedNestedItems]);

  const toggleNestedItem = (itemPath: string) => {
    setExpandedNestedItems((prev) =>
      prev.includes(itemPath)
        ? prev.filter((path) => path !== itemPath)
        : [...prev, itemPath]
    );
  };

  // Filter items based on permissions (including checking children)
  const accessibleItems = group.items.filter((item) => {
    // Check if the item itself is accessible
    const itemAccessible = item.permission
      ? userPermissions.includes(item.permission)
      : true;

    // If item has children, check if at least one child is accessible
    if (item.children && item.children.length > 0) {
      const hasAccessibleChild = item.children.some((child) =>
        child.permission ? userPermissions.includes(child.permission) : true
      );
      return itemAccessible || hasAccessibleChild;
    }

    return itemAccessible;
  });

  // Calculate total notifications for the group (including nested children)
  const groupNotificationCount = accessibleItems.reduce((total, item) => {
    const count = getNotificationCount(item, notificationSources);
    return total + (count || 0);
  }, 0);

  if (accessibleItems.length === 0) return null;

  const showContent = isExpanded || forceExpanded;

  return (
    <div className={isCompact ? "mb-1" : "mb-1.5"}>
      {!forceExpanded && (
        <button
          onClick={onToggle}
          className={cn(
            "w-full flex items-center gap-2 px-2 text-sm font-semibold rounded-md sidebar-nav-item",
            isCompact ? "py-1" : "py-2",
            "text-foreground/90 hover:text-foreground",
            "focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2",
          )}
        >
          {showContent ? (
            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 opacity-70" />
          )}
          {/* Icon size adjusted to match text better */}
          <div className="opacity-80">{group.icon}</div>
          <span className="flex-1 text-left">
            {t(group.titleKey, group.defaultTitle)}
          </span>
          {groupNotificationCount > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary/10 px-1 text-[10px] font-medium text-primary">
              {groupNotificationCount}
            </span>
          )}
        </button>
      )}

      {/* If forceExpanded (search mode), we might want to hide the group header if it's just filtered list, OR keep it to show context. Let's keep it for now but maybe style differently? Actually if forceExpanded is true, typically we just show the list. But user might want to see Group Name. Let's keep header but make it non-interactive if forced? No, keep interaction. */}
      
      {showContent && (
        <div
          className={cn("mt-0.5 pl-6", isCompact ? "space-y-0" : "space-y-0.5")}
        >
          {accessibleItems.map((item) => {
            // Use NestedNavItem for items with children
            if (item.children && item.children.length > 0) {
              return (
                <NestedNavItem
                  key={item.to}
                  item={item}
                  isExpanded={expandedNestedItems.includes(item.to)}
                  onToggle={() => toggleNestedItem(item.to)}
                  userPermissions={userPermissions}
                  notificationSources={notificationSources}
                  getNotificationCount={getNotificationCount}
                  onLinkClick={onLinkClick}
                  isCompact={isCompact}
                />
              );
            }

            // Regular nav item without children
            const notificationCount = getNotificationCount(
              item,
              notificationSources,
            );

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onLinkClick}
                className={({ isActive }) =>
                  cn(
                    "relative flex items-center gap-2.5 rounded-md px-2 group sidebar-nav-item",
                    isCompact ? "py-1 text-xs" : "py-2 text-sm",
                    "hover:text-foreground",
                    isActive
                      ? "active bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground",
                  )
                }
              >
                <span className={cn("flex h-4 w-4 items-center justify-center transition-colors", "text-muted-foreground/70 group-hover:text-foreground")}>
                  {item.icon}
                </span>
                <span className="flex-1 truncate">
                  {t(item.textKey, item.defaultText)}
                </span>
                {typeof notificationCount === "number" &&
                  notificationCount > 0 && (
                    <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                      {notificationCount.toString()}
                    </span>
                  )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollapsibleNavGroup;
